<template>
  <div class="row">
    <Checkbox
      ref="checkboxRef"
      :name="schema.parentConsent.name || 'parentConsent'"
      v-model="parentConsent"
      :schema="schema.parentConsent"
      :disabled="disabled"
      @change="handleChange(parentConsent)"
    >
      <template #label>
        {{ schema.parentConsent.label }}
      </template>
    </Checkbox>

    <div
      class="consent-details"
      v-if="
        entityTypeId === ConsentServiceEntityTypeIds.Draw &&
        mode !== FormModes.add
      "
      :disabled="disabled"
    >
      <div class="consent-detail">
        <span>Consented by:&nbsp;</span
        >{{ details?.personName || details?.consentedBy || "N/A" }}
      </div>
      <div class="consent-detail">
        <span>Consented at:&nbsp;</span>{{ details?.createdAtDisplay || "N/A" }}
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, toRefs } from "vue";
import Checkbox from "@/components/inputs/Checkbox.vue";
import { ConsentServiceEntityTypeIds, ConsentTypeIds } from "@/types/consents";
import type { ConsentFormVM } from "@/types/consents";
import type { FieldSchema, FormSchema } from "@/models/form";
import useConsents from "@/composables/consents/consentsHelpers";
import { useApplicationStore } from "@/store/store/applicationVMStore";
import { FormModes } from "@/types/form";

const checkboxRef = ref<InstanceType<typeof Checkbox>>();

const applicationStore = useApplicationStore();

const props = withDefaults(
  defineProps<{
    disabled: boolean;
    modelValue: boolean | undefined;
    entityTypeId: number;
    entityId: number;
    details?: ConsentFormVM;
    mode: keyof typeof FormModes;
    required?: boolean;
  }>(),
  {
    disabled: false,
    modelValue: undefined,
		required: true,
  }
);

const emit = defineEmits<{
  (e: "update:modelValue", value: undefined | boolean): void;
}>();

const { entityId } = toRefs(props);
const parentConsent = ref<boolean | undefined>(undefined);
const parentConsentId = ref<number>();

const errors = computed<Record<string, string>>(
  () => checkboxRef.value?.errors || {}
);

const schema = ref<FormSchema>({
  parentConsent: {
    name: "parentConsent",
    type: "checkbox",
    label:
      "By checking this box, you affirm that you are the student's parent or legal guardian and that you claim the student as a dependent on your tax return.",
    errorMessage: "You must check the box to continue.",
    required: true,
    validate: (schema: FieldSchema) => {
      if (!props.required) {
        schema.valid = true;
        schema.errors = [];
        return;
      }
      schema.valid = !errors.value.parentConsent;
      schema.errors = errors.value.parentConsent
        ? [errors.value.parentConsent]
        : [];

      if (!schema.valid) {
        schema.touched = true;
      } else {
        schema.valid = undefined;
        schema.errors = [];
      }
    },
  } as FieldSchema,
} as FormSchema);

const { addParentAttestationConsentAsync } = useConsents(entityId);

function handleChange(consentGiven: undefined | boolean) {
  emit("update:modelValue", consentGiven);
}

async function getParentAttestationConsents() {
  const parentConsentTypeId = ConsentTypeIds.PARENT_ATTESTATION;

  parentConsentId.value = parentConsentTypeId;

  const parentAttestationConsent =
    applicationStore?.application?.tabApplicationViewModel?.student
      ?.parentAttestation;

  if (parentAttestationConsent) {
    parentConsent.value = parentAttestationConsent?.consentGiven;
    parentConsentId.value = parentAttestationConsent?.id;
    emit("update:modelValue", parentAttestationConsent?.consentGiven);
  } else {
    emit("update:modelValue", undefined);
  }
}

watch(
  () => props.modelValue,
  () => {
    parentConsent.value = props.modelValue;
  },
  {
    immediate: true,
  }
);

defineExpose({
  addParentAttestationConsentAsync,
  errors,
  getParentAttestationConsents,
  parentConsent,
});
</script>

<style scoped>
.consent-details {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 12px;
  font-family: "Montserrat";
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  padding-left: 24px;
}
.consent-details[disabled="true"] {
  color: #8c8c8c;
}
.consent-detail span:first-of-type {
  font-weight: 800;
}
</style>
